import React from 'react';
import dynamic from 'next/dynamic';
import { cleanStegaDataRecursively } from '@workspace/lib/stega-utils';

type BlocksType = {
  [key: string]: React.ComponentType<any>;
};

type BlockType = {
  _type: string;
  _key: string;
};

type BlockProps = {
  index: number;
  block: BlockType;
  pageId: string;
  pageType: string;
};

// Dynamic imports of all CMS blocks to optimize bundle size and TBT
const TextSection = dynamic(() => import('./TextSection').then((mod) => mod.TextSection));
const ImageTextBlock = dynamic(() => import('./ImageTextBlock').then((mod) => mod.ImageTextBlock));
const DocumentBlock = dynamic(() => import('./DocumentsBlock').then((mod) => mod.DocumentBlock));
const Gallery = dynamic(() => import('./Gallery').then((mod) => mod.Gallery));
const ImageGrid = dynamic(() => import('./ImagesGrid').then((mod) => mod.ImageGrid));
const ProductSummary = dynamic(() => import('./ProductSummary').then((mod) => mod.ProductSummary));
const Applications = dynamic(() => import('./PageApplications').then((mod) => mod.Applications));
const BinOptions = dynamic(() => import('./BinOptions').then((mod) => mod.BinOptions));
const PageContactSupport = dynamic(() => import('./ContactSupport').then((mod) => mod.PageContactSupport));
const ProductVariant = dynamic(() => import('./ProductVariant').then((mod) => mod.ProductVariant));
const MiddleBanner = dynamic(() => import('./MiddleBanner').then((mod) => mod.MiddleBanner));
const FeatureSummary = dynamic(() => import('./FeatureSummary').then((mod) => mod.FeatureSummary));
const ProductType = dynamic(() => import('./ProductType').then((mod) => mod.ProductType));
const VideoImageRenderer = dynamic(() => import('./VideoImageRenderer').then((mod) => mod.VideoImageRenderer));
const ContentCarouselBlock = dynamic(() => import('./ContentCarousel').then((mod) => mod.ContentCarouselBlock));
const HeroBannerCarousel = dynamic(() => import('./HeroBannerCarousel').then((mod) => mod.HeroBannerCarousel));
const TextIconList = dynamic(() => import('./TextIconList').then((mod) => mod.TextIconList));
const MasterTextBlock = dynamic(() => import('./MasterTextBlock').then((mod) => mod.MasterTextBlock));
const ProductSlideshow = dynamic(() => import('./ProductSlideshow').then((mod) => mod.ProductSlideshow));
const ProductCarousel = dynamic(() => import('./ProductCarousel').then((mod) => mod.ProductCarousel));
const MultiColumnBanner = dynamic(() => import('./MultiColumnBanner').then((mod) => mod.MultiColumnBanner));
const HeadlineRenderer = dynamic(() => import('./HeadlineRenderer').then((mod) => mod.HeadlineRenderer));
const MilestoneRenderer = dynamic(() => import('./MilestoneRenderer').then((mod) => mod.MilestoneRenderer));
const QuoteRenderer = dynamic(() => import('./Quote').then((mod) => mod.QuoteRenderer));
const LogoWall = dynamic(() => import('./LogoWall').then((mod) => mod.LogoWall));
const AskGregSectionRenderer = dynamic(() => import('./AskGreg').then((mod) => mod.AskGregSectionRenderer));
const Newsletter = dynamic(() => import('./Newsletter').then((mod) => mod.Newsletter));
const ContactFormBlock = dynamic(() => import('./ContactFormBlock').then((mod) => mod.ContactFormBlock));
const IconTextColumnsBlock = dynamic(() => import('./IconTextColumnsBlock').then((mod) => mod.IconTextColumnsBlock));

const Blocks: BlocksType = {
  textSection: TextSection,
  imageTextBlock: ImageTextBlock,
  documentBlock: DocumentBlock,
  gallery: Gallery,
  imageGrid: ImageGrid,
  productSummary: ProductSummary,
  applicationsBlock: Applications,
  binOptions: BinOptions,
  contactSupportBlock: PageContactSupport,
  // heroBannerBlock: HeroBannerBlock,
  productVariantSection: ProductVariant,
  middleBanner: MiddleBanner,
  featureSummary: FeatureSummary,
  productType: ProductType,
  videoImageBlock: VideoImageRenderer,
  contentCarousel: ContentCarouselBlock,
  heroBannerSlideshow: HeroBannerCarousel,
  textIconList: TextIconList,
  masterTextBlock: MasterTextBlock,
  productSlideshow: ProductSlideshow,
  productCarousel: ProductCarousel,
  multiColumnBanner: MultiColumnBanner,
  headline: HeadlineRenderer,
  milestoneTimeline: MilestoneRenderer,
  quote: QuoteRenderer,
  logoWall: LogoWall,
  askGreg: AskGregSectionRenderer,
  newsletter: Newsletter,
  contactFormBlock: ContactFormBlock,
  iconTextColumnsBlock: IconTextColumnsBlock,
};

/**
 * Used by the <PageBuilder>, this component renders a the component that matches the block type.
 * Automatically applies stega cleaning to all block data for live preview compatibility.
 */
export default function BlockRenderer({ block, index }: BlockProps) {
  if (!block || !block._type) return null;

  const cleanBlock = cleanStegaDataRecursively(block);
  const blockType =
    typeof cleanBlock._type === 'string' ? (cleanBlock._type.split('.').pop() ?? cleanBlock._type) : cleanBlock._type;

  // Block does exist
  if (typeof Blocks[blockType] !== 'undefined') {
    return (
      <div key={cleanBlock._key}>
        {React.createElement(Blocks[blockType], {
          key: cleanBlock._key,
          block: cleanBlock,
          index: index,
        })}
      </div>
    );
  }
  // Block doesn't exist yet
  return React.createElement(
    () => (
      <div className="w-full bg-secondary-100 text-center text-secondary-50 p-20 rounded">
        A &ldquo;{cleanBlock._type}&rdquo; block hasn&apos;t been created
      </div>
    ),
    { key: cleanBlock._key },
  );
}
